import {Component, Input, OnInit, ChangeDetectorRef} from '@angular/core';
import {ModalController, NavParams, ToastController} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from '@angular/forms';
import {ShippingService} from 'src/app/services/shipping.service';
import {PaymentService} from 'src/app/services/shipment-payment.service';
import {ProductModel} from 'src/app/models/wardrobe.model';
import {
  loadStripe, Stripe, StripeElements,
  StripeIdealBankElement
} from '@stripe/stripe-js';
import {config} from 'process';
import {firstValueFrom, combineLatest} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {CountryPickerModalComponent} from 'src/app/components/country-picker-modal/country-picker-modal.component';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-buyer-shipping-address-payment',
  templateUrl: './buyer-shipping-address-payment.component.html',
  styleUrls: ['./buyer-shipping-address-payment.component.scss'],
  host: { class: 'ship-modal' }
})
export class BuyerShippingAddressPaymentComponent implements OnInit {
  form: FormGroup;
  // Tijdelijk beperkt tot NL (19 aug) -- overige landen (incl. België) uitgezet
  // totdat er land-afhankelijke verzendprijzen gebouwd zijn (zie overdrachtsdocument).
  countries = [
    { code: 'NL', name: 'Nederland' },
  ];
  shippingMethods: any[] = [];
  selectedMethodId: string | null = null;
  private pendingShippingMethodId: string | null = null;
  isLoading = false;
  orderType = '';
  saveAddress = 0;


  @Input() userId!: number;
  @Input() productId!: number;
  @Input() offered_product_id!: number;
  @Input() product!: ProductModel;
  @Input() chatId!: string;
  @Input() userType!: string;
  @Input() formSubmittedSuccessfully!: boolean;

  isModalOpen = true;
  selectedShippingPrice: number = 0;
  ShippingMaxWeight: number = 0;
  swopFee: number = 0;

  stripe: Stripe | null = null;
  elements!: StripeElements;
  ideal!: StripeIdealBankElement;
  // elements!: StripeElements;
  // ideal!: StripeIdealBankElement;
  // clientSecret = '';
  // idealEmail: string = '';
  // formSubmittedSuccessfully = false;

  showIdealUI = false;
  addressLookupLoading = false;

  constructor(
    private navParams: NavParams,
    private fb: FormBuilder,
    private modalCtrl: ModalController,
    private sendCloudService: ShippingService,
    private paymentService: PaymentService,
    protected translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private http: HttpClient,
    private toastController: ToastController
  ) {
    this.initializeForm();
    this.orderType = this.navParams.get('orderType') || '';
    console.log('Order Type:', this.orderType);
  }

  async ngOnInit() {
    this.fetchShippingMethods();
    this.setupAddressAutofill();
    if (!this.productId || !this.userId) {
      console.error('Missing productId or userId');
      return;
    }
    this.loadExistingShippingDetails();
  }

  // Vult het formulier vooraf als er al een (onbetaald) adres/label bestaat voor
  // deze swop, zodat een retry na een mislukte betaling niet blanco begint.
  loadExistingShippingDetails(): void {
    this.sendCloudService.getShippingData('buying', this.userId, this.productId, this.offered_product_id ?? null).subscribe({
      next: (response: any) => {
        const details = response?.shipping_details;
        if (!details) {
          return;
        }
        this.form.patchValue({
          name: details.name ?? '',
          address: details.address ?? '',
          house_number: details.house_number ?? '',
          postalCode: details.postal_code ?? '',
          city: details.city ?? '',
          country: details.country ?? 'NL',
          phone: details.phone_number ?? '',
          shippingMethod: details.shipping_method_id ?? null,
        }, { emitEvent: false });

        if (details.shipping_method_id) {
          this.pendingShippingMethodId = details.shipping_method_id;
          this.applySelectedMethodPricing(this.pendingShippingMethodId);
        }
      },
      error: (err) => {
        console.error('Error fetching existing shipping details:', err);
      },
    });
  }

  applySelectedMethodPricing(id: string): void {
    const method = this.shippingMethods.find((m) => m.id === id);
    if (method) {
      this.selectedMethodId = method.id;
      this.selectedShippingPrice = parseFloat(method.price);
      this.ShippingMaxWeight = parseFloat(method.max_weight);
    }
  }

  setupAddressAutofill() {
    combineLatest([
      this.form.get('postalCode')!.valueChanges,
      this.form.get('house_number')!.valueChanges
    ]).pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.tryAutofillDutchAddress();
    });
  }

  tryAutofillDutchAddress() {
    const country = this.form.get('country')?.value;
    const postalCodeRaw = this.form.get('postalCode')?.value || '';
    const postalCode = postalCodeRaw.replace(/\s/g, '').toUpperCase();
    const houseNumber = this.form.get('house_number')?.value;

    if (country !== 'NL') return;
    if (!/^[1-9][0-9]{3}[A-Za-z]{2}$/.test(postalCode)) return;
    if (!houseNumber) return;

    this.addressLookupLoading = true;
    const query = `${postalCode} ${houseNumber}`;
    this.http.get<any>(`https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${encodeURIComponent(query)}&rows=1&fq=type:adres`)
      .subscribe({
        next: (res) => {
          this.addressLookupLoading = false;
          const doc = res?.response?.docs?.[0];
          if (doc) {
            this.form.patchValue({
              address: doc.straatnaam || this.form.get('address')?.value,
              city: doc.woonplaatsnaam || this.form.get('city')?.value
            }, { emitEvent: false });
          }
        },
        error: () => {
          this.addressLookupLoading = false;
        }
      });
  }

  initializeForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.pattern("^[A-Za-zÀ-ÿ\\s'-]{2,}$")]],
      address: ['', Validators.required],
      house_number: ['', Validators.required],
      postalCode: ['', [Validators.required]],
      city: ['', [Validators.required, Validators.pattern("^[A-Za-zÀ-ÿ\\s'-]{2,}$")]],
      country: ['NL', Validators.required],
      phone: ['', [this.optionalPhoneValidator]],
      shippingMethod: [null, Validators.required],
    });

    this.form.get('country')?.valueChanges.subscribe((country: string) => {
      this.updatePostalCodeValidator(country);
    });
    this.updatePostalCodeValidator(this.form.get('country')?.value);
  }

  updatePostalCodeValidator(country: string) {
    const postalCodeControl = this.form.get('postalCode');
    if (!postalCodeControl) return;
    if (country === 'NL') {
      postalCodeControl.setValidators([Validators.required, Validators.pattern('^[1-9][0-9]{3}\\s?[A-Za-z]{2}\\s?$')]);
    } else {
      postalCodeControl.setValidators([Validators.required]);
    }
    postalCodeControl.updateValueAndValidity();
  }

  async openCountryPicker() {
    const modal = await this.modalCtrl.create({
      component: CountryPickerModalComponent,
      componentProps: {
        countries: this.countries,
        selectedCode: this.form.get('country')?.value,
      },
      cssClass: 'country-picker-modal-wrapper',
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.code) {
      this.form.get('country')?.setValue(data.code);
    }
  }

  getCountryName(code: string): string {
    return this.countries.find(c => c.code === code)?.name || '';
  }

  optionalPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const pattern = /^((\\+31\\s?6\\s?[0-9]{8})|(06\\s?[0-9]{8})|\\+?[0-9\\s-]{6,15})$/;
    return pattern.test(value) ? null : { pattern: true };
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const formDetails = this.form.value;

    const payload = {
      delivery_type: this.orderType,
      name: formDetails.name,
      address: formDetails.address,
      house_number: formDetails.house_number,
      // street: formDetails.street,
      postal_code: formDetails.postalCode,
      city: formDetails.city,
      country: formDetails.country,
      phone_number: formDetails.phone,
      shipping_method_id: formDetails.shippingMethod,
      chat_id: this.chatId,
      // shipping_status: this.orderType === 'delivery' ? 'Delivery' : 'Pick Up',
      save_address: this.saveAddress,
      order_Status: 0,
      userType: this.userType,
    };

    try {
      await firstValueFrom(
        await this.sendCloudService.sendOrderToCloud(
          this.userId,
          this.productId,
          this.offered_product_id,
          this.userType,
          this.orderType,
          this.saveAddress,
          payload
        )
      );
      const mollieResponse = await firstValueFrom(
        this.paymentService.startMolliePayment({
          user_id: this.userId,
          product_id: this.productId,
          shipping_method_id: this.selectedMethodId,
          type: this.userType
        })
      );

      const redirectUrl = mollieResponse?.redirect_url;

      if (redirectUrl) {
        console.log(redirectUrl, 'provide address and pay');
        this.formSubmittedSuccessfully = true;
        this.dismiss();
        if ((window as any).cordova && (window as any).cordova.InAppBrowser) {
          (window as any).cordova.InAppBrowser.open(redirectUrl, '_system');
        } else {
          window.open(redirectUrl, '_system');
        }
      } else {
        console.error('Missing redirect URL in Mollie response');
        const toast = await this.toastController.create({
          message: 'Betaling kon niet gestart worden. Probeer het opnieuw.',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    } catch (error) {
      console.error('Error submitting shipping details:', error);
      const toast = await this.toastController.create({
        message: 'Er is iets misgegaan. Probeer het opnieuw.',
        duration: 3000,
        color: 'danger',
      });
      await toast.present();
    } finally {
      this.isLoading = false;
    }

  }

  fetchShippingMethods(): void {
    this.sendCloudService.getShippingMethods().subscribe({
      next: (res: any) => {
        this.shippingMethods = res?.shipping_methods || [];
        this.swopFee = parseFloat(res?.swop_fee || 0);
        if (this.pendingShippingMethodId) {
          this.applySelectedMethodPricing(this.pendingShippingMethodId);
        }
      },
      error: (err) => {
        console.error('Error fetching shipping methods', err);
      },
    });
  }

  selectMethod(method: any): void {
    this.selectedMethodId = method.id;
    this.form.get('shippingMethod')?.setValue(method.id);
    this.selectedShippingPrice = parseFloat(method.price);
    this.ShippingMaxWeight = parseFloat(method.max_weight);
  }

  dismiss() {
    this.modalCtrl.dismiss({isModalOpen: false});

  }

  ionViewWillLeave() {
    this.isModalOpen = false;
  }
}


